/**
 * Application chrome: title bar, menu bar with cascading submenus, screen
 * switcher, and status bar — the frame every screen from artboard 1a sits in.
 */

import { useEffect, useRef, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { canCompute, ROLE_LABELS, useAuth } from "../auth";
import { Icon, Logo, Wordmark } from "../components/icons";
import { Caret } from "../components/ui";
import type { Project, Well } from "../types";

export type ScreenKey = "dashboard" | "log" | "crossplot" | "histogram" | "correlation"
  | "map" | "analysis" | "geomodel" | "estimate" | "lrlc" | "image" | "core";

const SCREENS: { key: ScreenKey; label: string; path: string; icon: () => ReactNode }[] = [
  { key: "dashboard", label: "Dashboard", path: "dashboard", icon: () => Icon.analysis({ size: 13 }) },
  { key: "log", label: "Log", path: "log", icon: () => Icon.logs({ size: 13 }) },
  { key: "crossplot", label: "Crossplot", path: "crossplot", icon: () => Icon.crossplot({ size: 13 }) },
  // Histogram berdiri sendiri di sebelah crossplot, sebab ia dipakai untuk
  // pekerjaan yang lain: crossplot menjawab "bagaimana dua besaran ini
  // berhubungan", histogram menjawab "berapa nilainya, dan sebarannya
  // seperti apa" — dan yang kedua adalah pertanyaan yang menentukan cutoff.
  { key: "histogram", label: "Histogram", path: "histogram", icon: () => Icon.table({ size: 13 }) },
  { key: "correlation", label: "Correlation", path: "correlation", icon: () => Icon.correlation({ size: 13 }) },
  { key: "map", label: "Map", path: "map", icon: () => Icon.map({ size: 13 }) },
  { key: "analysis", label: "Analysis", path: "analysis", icon: () => Icon.analysis({ size: 13 }) },
  { key: "geomodel", label: "3D model", path: "geomodel", icon: () => Icon.map({ size: 13 }) },
  { key: "estimate", label: "Estimation", path: "estimate", icon: () => Icon.analysis({ size: 13 }) },
  // LRLC berdiri sesudah Estimation dan bukan di dalam Analysis, sebab ia
  // menjawab pertanyaan yang berbeda dari keduanya: bukan "berapa nilainya"
  // melainkan "di mana log berbohong". Sebuah lapisan yang terbaca berair
  // karena lempung penghantar atau laminasi tipis tidak pernah muncul sebagai
  // kejanggalan pada alur deterministik — ia muncul sebagai jawaban yang rapi
  // dan salah.
  { key: "lrlc", label: "LRLC", path: "lrlc", icon: () => Icon.crossplot({ size: 13 }) },
  // Citra dan core berdiri di ujung, berdampingan, sebab keduanya dibaca
  // bergantian: sebuah goresan konduktif pada FMI adalah rekahan atau laminasi
  // lempung, dan core-lah yang memutuskannya.
  { key: "image", label: "Image", path: "image", icon: () => Icon.logs({ size: 13 }) },
  { key: "core", label: "Core", path: "core", icon: () => Icon.table({ size: 13 }) },
];

/** One row of a curve-editing menu, before it is turned into a MenuItem. */
interface EditEntry {
  key?: string;
  label?: string;
  separator?: boolean;
  childrenLabel?: string;
  children?: EditEntry[];
  /** Stays live on a well that holds no curves — it is how curves get there. */
  worksEmpty?: boolean;
}

interface MenuItem {
  label: string;
  shortcut?: string;
  disabled?: boolean;
  /** Alasan item ini mati, dibacakan sewaktu kursor lewat. Item kelabu tanpa
   *  alasan membuat pembacanya menebak: hak akses, data, atau salah layar? */
  hint?: string;
  onClick?: () => void;
  separator?: boolean;
  children?: MenuItem[];
  /** Heading above a submenu. Absent means the submenu carries no heading —
   *  "MODEL" is true of the multi-mineral list and of nothing else. */
  childrenLabel?: string;
}

export function AppShell({
  project,
  wells,
  activeWell,
  onWellChange,
  screen,
  toolbar,
  status,
  children,
  onAction,
}: {
  project: Project | null;
  wells: Well[];
  activeWell: Well | null;
  onWellChange: (wellId: number) => void;
  screen: ScreenKey;
  toolbar?: ReactNode;
  status?: ReactNode;
  children: ReactNode;
  onAction?: (action: string) => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, setTheme } = useAuth();
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!openMenu) return;
    const close = (event: MouseEvent) => {
      if (!barRef.current?.contains(event.target as Node)) setOpenMenu(null);
    };
    const escape = (event: KeyboardEvent) => event.key === "Escape" && setOpenMenu(null);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", escape);
    };
  }, [openMenu]);

  const go = (path: string) => {
    if (project) navigate(`/projects/${project.id}/${path}`);
  };

  const mayCompute = canCompute(project?.my_role);
  const undrilled = activeWell?.status === "planned";
  const hasNoCurves =
    !activeWell ||
    activeWell.curve_sets.reduce((sum, set) => sum + set.curve_count, 0) === 0;

  /**
   * Build one of the three curve-editing menus.
   *
   * Every item is the same kind of thing — open one dialog, on the active well
   * — so the reason an item is dead is written once here rather than fifteen
   * times. An item greyed out with no reason given leaves the reader guessing
   * between their role, their well and their screen.
   *
   * The reason turns on what is missing, not on the well's status. A planned
   * well and a drilled well whose LAS has not been imported are both empty,
   * but the way out of each is a different door, and a message that names the
   * wrong one sends the reader looking for a file that does not exist.
   */
  function editMenu(items: EditEntry[]): MenuItem[] {
    const reason = !mayCompute
      ? "Only owners and petrophysicists may edit curves"
      : undefined;
    const emptyReason = hasNoCurves
      ? undrilled
        ? `${activeWell?.name} is a planned well — use Curve insert to make a prognosis curve`
        : `${activeWell?.name} has no curve yet — import a LAS first, or make one with Curve insert`
      : undefined;
    const build = (entry: EditEntry): MenuItem => {
      if (entry.separator) return { separator: true, label: "" };
      // A submenu is live if any of its children are, so "Split and merge"
      // does not go dead over a rule that only applies to what is inside it.
      const children = entry.children?.map(build);
      const own = reason ?? (entry.worksEmpty ? undefined : emptyReason);
      const dead = children ? children.every((child) => child.disabled) : Boolean(own);
      return {
        label: entry.label ?? "",
        childrenLabel: entry.childrenLabel,
        disabled: dead,
        hint: dead ? (own ?? children?.[0]?.hint) : undefined,
        children,
        onClick: entry.key ? () => onAction?.(`curve-op:${entry.key}`) : undefined,
      };
    };
    return items.map(build);
  }

  const menus: Record<string, MenuItem[]> = {
    File: [
      // Tanpa pintasan tertulis. Ctrl+O, Ctrl+1 dan Ctrl+2 pernah tercantum di
      // ketiga menu ini tanpa pernah dipasang di mana pun: tidak ada satu pun
      // penangan papan tik global di aplikasi ini. Dialog "Pintasan & gerakan"
      // sudah dibersihkan dari ketiganya dengan alasan yang sama — sebuah
      // pintasan yang diiklankan lalu tidak bekerja membuat orang meragukan
      // tombol yang sungguh ada — dan menunya terlewat.
      { label: "Projects…", onClick: () => navigate("/projects") },
      { label: "Import LAS…", disabled: !mayCompute, onClick: () => onAction?.("import-las") },
      { separator: true, label: "" },
      {
        // Servernya menolak lembar CPI sumur rencana bukan karena kurvanya
        // kurang — kurva prognosis boleh ada — melainkan karena lembarnya
        // memuat net pay dan sebaris tanda tangan atas lubang yang belum dibor.
        // Pintunya ditutup di sini supaya penolakan itu tidak perlu dibaca di
        // tab kosong.
        label: "Print CPI (A3)…",
        disabled: undrilled,
        hint: undrilled ? `${activeWell?.name} is a planned well — not drilled yet` : undefined,
        onClick: () => onAction?.("print-cpi"),
      },
      {
        // Tidak bergantung pada sumur aktif: yang dipilih di sana adalah
        // sumur-sumurnya sendiri, dan sebuah menu yang mati karena belum ada
        // sumur aktif menutup pintu ke layar yang tidak memerlukannya.
        label: "Print CPI for many wells…",
        onClick: () => onAction?.("print-cpi-multi"),
      },
      { label: "Export map (PDF)", onClick: () => onAction?.("export-map") },
    ],
    // Keenamnya membuka jendela yang sama pada tab yang berbeda. Marker, zona
    // dan set kurva adalah benda lintas-sumur — pertanyaan tentangnya hampir
    // selalu "di sumur mana ia belum ada" — dan jendela terpisah per jenis
    // memaksa orang menutup satu untuk membandingkannya dengan yang lain.
    Data: [
      { label: "Fields…", onClick: () => onAction?.("data:fields") },
      { label: "Wells…", onClick: () => onAction?.("data:wells") },
      { separator: true, label: "" },
      { label: "Well markers…", onClick: () => onAction?.("data:markers") },
      { label: "Zones…", onClick: () => onAction?.("data:zones") },
      { label: "Curve sets…", onClick: () => onAction?.("data:sets") },
      // Berdiri di bawah "Curve sets…" dan bukan di dalamnya: keduanya
      // berdekatan dan menjawab pertanyaan yang berbeda. Di sana yang dipilih
      // adalah **versi** — hasil hitungan mana yang berlaku — sedangkan di
      // sini yang dinilai adalah **alatnya**, dan jawabannya sama untuk
      // seluruh versi. Sakelar satuannya ada di pohon proyek; yang ini untuk
      // saat keputusannya menyangkut satu mnemonik di banyak sumur sekaligus.
      { label: "Logs…", onClick: () => onAction?.("data:logs") },
    ],
    Wells: [
      ...wells.slice(0, 12).map((well) => ({
        label: well.name,
        shortcut: well.id === activeWell?.id ? "active" : undefined,
        onClick: () => onWellChange(well.id),
      })),
      // Menu ini memuat dua belas sumur pertama dan diam tentang sisanya, dan
      // daftar yang terpotong diam-diam terlihat persis seperti daftar yang
      // lengkap. Pada lapangan berisi enam puluh sumur, baris inilah pintunya.
      ...(wells.length > 12
        ? [
            { separator: true, label: "" },
            {
              label: `All ${wells.length} wells…`,
              onClick: () => onAction?.("data:wells"),
            },
          ]
        : []),
    ],
    "QC & Edit Data": editMenu([
      { key: "insert", label: "Curve insert…", worksEmpty: true },
      { key: "despike", label: "Despiking…" },
      { key: "baseline", label: "Baseline shift…" },
      { separator: true },
      {
        label: "Split and merge",
        children: [
          { key: "split", label: "Split — cut by depth…" },
          { key: "merge", label: "Merge — join curves…" },
        ],
      },
      { key: "depth_shift", label: "Depth shift…" },
      { separator: true },
      { key: "reconstruct", label: "Log reconstruction…" },
    ]),
    Precalculation: editMenu([
      { key: "depth_units", label: "Convert depth units…" },
      { key: "resample", label: "Resample…" },
      { separator: true },
      { key: "calculator", label: "Curve calculator…" },
      { key: "tvd", label: "TVD / TVDSS…" },
      { separator: true },
      { key: "trim", label: "Null & pangkas…" },
    ]),
    "Environment Corrections": editMenu([
      { key: "env_gr", label: "Gamma ray — lubang bor…" },
      { key: "env_neutron", label: "Neutron — lubang bor…" },
      { key: "env_density", label: "Densitas — mudcake…" },
      { separator: true },
      { key: "env_resistivity", label: "Resistivitas — suhu (Arps)…" },
    ]),
    Analysis: [
      {
        label: "Vsh from GR / Neutron",
        disabled: !mayCompute,
        onClick: () => onAction?.("run-deterministic"),
      },
      {
        label: "Porosity & saturation",
        disabled: !mayCompute,
        onClick: () => onAction?.("run-deterministic"),
      },
      { separator: true, label: "" },
      {
        label: "Multi-mineral analysis",
        disabled: !mayCompute,
        childrenLabel: "MODEL",
        children: [
          { label: "Three minerals (Q-F-C)", onClick: () => onAction?.("multimin:qfc") },
          { label: "Complex lithology", onClick: () => onAction?.("multimin:complex") },
          { label: "Probabilistic inversion", disabled: true },
        ],
      },
      { label: "Cutoffs & reservoir totals", onClick: () => go("analysis") },
      // Dahulu mati tanpa alasan; sekarang layarnya ada, di menu Image & Core.
      { label: "Core–log calibration", onClick: () => go("core") },
      { separator: true, label: "" },
      { label: "Batch multi-well", disabled: !mayCompute, onClick: () => onAction?.("batch") },
    ],
    // Menu tersendiri, di antara Analysis dan Image & Core, dan bukan sisipan
    // di bawah Analysis. Yang dijawabnya bukan "berapa nilainya" melainkan "di
    // mana log berbohong": sebuah lapisan yang terbaca berair karena lempung
    // penghantar atau laminasi tipis tidak pernah muncul sebagai kejanggalan
    // pada alur deterministik — ia muncul sebagai jawaban yang rapi dan salah.
    // Letaknya persis di antara keduanya sebab di situlah pekerjaannya jatuh:
    // sesudah kurva dihitung, sebelum citra dan core dipakai memutuskannya.
    LRLC: [
      { label: "Low-resistivity pay…", onClick: () => onAction?.("lrlc:screen") },
      {
        // Membuka sumur **aktif**, bukan sumur pertama yang tercentang di
        // layarnya. Sebuah butir menubar yang membuka sumur lain daripada yang
        // tertulis di pemilih sumur di sebelahnya adalah butir yang tidak bisa
        // dipercaya dua kali.
        label: "Log sheet…",
        disabled: undrilled,
        hint: undrilled
          ? `${activeWell?.name} is a planned well — not drilled yet`
          : undefined,
        onClick: () => onAction?.("lrlc:sheet"),
      },
      { separator: true, label: "" },
      // Data, bukan hasil hitungan: apa yang benar-benar naik ke permukaan
      // ketika selangnya dibuka. Ia satu-satunya yang memisahkan "LRLC
      // potential" dari "LRLC proven", dan karena itu pintunya tidak menunggu
      // penggolongnya dijalankan lebih dulu.
      { label: "Production tests…", onClick: () => onAction?.("lrlc:tests") },
    ],
    // Menu tersendiri, bukan sisipan di bawah Analysis. Yang dikerjakan di sini
    // bukan perhitungan petrofisika melainkan pembacaan struktur — dip, rekahan,
    // tegangan, dan batuan yang benar-benar terangkat — dan pekerjaan itu punya
    // datanya sendiri, layarnya sendiri, dan orang yang mengerjakannya sendiri.
    "Image & Core": [
      {
        label: "Borehole image…",
        disabled: undrilled,
        hint: undrilled ? `${activeWell?.name} is a planned well — not drilled yet` : undefined,
        onClick: () => go("image"),
      },
      { label: "Core & calibration…", disabled: undrilled, onClick: () => go("core") },
      { separator: true, label: "" },
      {
        label: "Import image log (ASCII)…",
        disabled: !mayCompute,
        hint: !mayCompute ? "Only owners and petrophysicists may import" : undefined,
        onClick: () => onAction?.("import-image"),
      },
      {
        label: "Import core analysis (CSV)…",
        disabled: !mayCompute,
        onClick: () => onAction?.("import-plugs"),
      },
      { separator: true, label: "" },
      {
        label: "Dip & fracture statistics",
        disabled: undrilled,
        onClick: () => onAction?.("image:stats"),
      },
      {
        label: "Stress orientation (breakouts)",
        disabled: undrilled,
        onClick: () => onAction?.("image:stress"),
      },
      {
        label: "Publish fracture curves",
        disabled: !mayCompute,
        onClick: () => onAction?.("image:publish"),
      },
      { separator: true, label: "" },
      { label: "Core–log calibration", disabled: undrilled, onClick: () => go("core") },
    ],
    View: [
      { label: "Dark theme (Deep Section)", onClick: () => setTheme("dark") },
      { label: "Light theme (Core Paper)", onClick: () => setTheme("light") },
      { label: "Log sheet colours…", onClick: () => onAction?.("log-colors") },
      { separator: true, label: "" },
      ...SCREENS.map((item) => ({ label: item.label, onClick: () => go(item.path) })),
    ],
    Tools: [
      { label: "Parameter sets…", onClick: () => onAction?.("parameters") },
      { label: "Multi-mineral model…", onClick: () => onAction?.("mineral-model") },
      { label: "Project members…", onClick: () => onAction?.("members") },
      { label: "Activity history…", onClick: () => onAction?.("activity") },
    ],
    Help: [
      { label: "About EvoLoG", onClick: () => onAction?.("about") },
      { label: "Keyboard shortcuts", onClick: () => onAction?.("shortcuts") },
    ],
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: "var(--app-bg)" }}>
      {/* Title bar */}
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: 34,
          flex: "none",
          padding: "0 12px",
          background: "var(--titlebar)",
          borderBottom: "1px solid var(--line)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          <Logo />
          <Wordmark size={12} />
          <span
            style={{
              font: "400 11px var(--font-mono)",
              color: "var(--ink-6)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            / Project: {project?.name ?? "—"}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {project?.my_role && (
            <span style={{ font: "400 10.5px var(--font-mono)", color: "var(--ink-6)" }}>
              {ROLE_LABELS[project.my_role]}
            </span>
          )}
          <button
            type="button"
            title={user?.theme === "light" ? "Switch to the dark theme" : "Switch to the light theme"}
            onClick={() => setTheme(user?.theme === "light" ? "dark" : "light")}
            style={{
              display: "flex",
              alignItems: "center",
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "var(--ink-4)",
              padding: 2,
            }}
          >
            {user?.theme === "light" ? Icon.moon({ size: 13 }) : Icon.sun({ size: 13 })}
          </button>
          <div
            title={`${user?.display_name} — ${user?.discipline || "Member"}`}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 22,
              height: 22,
              borderRadius: "50%",
              background: "var(--accent-wash)",
              border: "1px solid var(--accent)",
              font: "600 9px var(--font-mono)",
              color: "var(--accent-pale)",
            }}
          >
            {user?.initials}
          </div>
          <button
            type="button"
            title="Sign out"
            onClick={logout}
            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ink-4)", padding: 2 }}
          >
            {Icon.logout({ size: 13 })}
          </button>
        </div>
      </header>

      {/* Menu bar */}
      <nav
        ref={barRef}
        style={{
          position: "relative",
          display: "flex",
          alignItems: "stretch",
          height: 28,
          flex: "none",
          background: "var(--menubar)",
          borderBottom: "1px solid var(--line)",
          padding: "0 6px",
          zIndex: 50,
        }}
      >
        {Object.entries(menus).map(([name, items]) => (
          <MenuButton
            key={name}
            name={name}
            items={items}
            open={openMenu === name}
            onOpen={() => setOpenMenu(name)}
            onHover={() => openMenu && setOpenMenu(name)}
            onClose={() => setOpenMenu(null)}
          />
        ))}
      </nav>

      {/* Toolbar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          height: 44,
          flex: "none",
          padding: "0 12px",
          background: "var(--toolbar)",
          borderBottom: "1px solid var(--line)",
        }}
      >
        <WellPicker wells={wells} activeWell={activeWell} onChange={onWellChange} />
        {toolbar}
        <span style={{ flex: 1 }} />
        <div
          style={{
            display: "flex",
            background: "var(--control-flat)",
            border: "1px solid var(--border-control)",
            borderRadius: 7,
            overflow: "hidden",
          }}
        >
          {SCREENS.map((item) => {
            const active = item.key === screen;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => go(item.path)}
                aria-current={active ? "page" : undefined}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "5px 11px",
                  border: "none",
                  background: active ? "var(--accent)" : "transparent",
                  color: active ? "var(--accent-contrast)" : "var(--ink-3)",
                  font: `${active ? 600 : 500} 11px var(--font-sans)`,
                  cursor: "pointer",
                }}
              >
                {item.icon()}
                {item.label}
              </button>
            );
          })}
        </div>
      </div>

      <main style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>{children}</main>

      {/* Status bar */}
      <footer
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          height: 24,
          flex: "none",
          padding: "0 12px",
          background: "var(--menubar)",
          borderTop: "1px solid var(--line)",
          font: "400 10px var(--font-mono)",
          color: "var(--ink-6)",
        }}
      >
        {status ?? <span>Siap</span>}
        <span style={{ flex: 1 }} />
        <span>
          {wells.length} wells · {project?.curve_count ?? 0} curves
        </span>
        <span>units: {project?.unit_system === "imperial" ? "ft · British" : "m · SI"}</span>
        <span>CRS: {project?.crs ?? "—"}</span>
        <span style={{ opacity: 0.6 }}>{location.pathname}</span>
      </footer>
    </div>
  );
}

function MenuButton({
  name,
  items,
  open,
  onOpen,
  onHover,
  onClose,
}: {
  name: string;
  items: MenuItem[];
  open: boolean;
  onOpen: () => void;
  onHover: () => void;
  onClose: () => void;
}) {
  const [submenu, setSubmenu] = useState<string | null>(null);
  return (
    <div style={{ position: "relative", display: "flex" }} onMouseEnter={onHover}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => (open ? onClose() : onOpen())}
        style={{
          display: "flex",
          alignItems: "center",
          padding: "0 11px",
          border: "none",
          background: open ? "var(--hover)" : "transparent",
          color: open ? "var(--ink)" : "var(--ink-3)",
          font: "500 11.5px var(--font-sans)",
          cursor: "pointer",
        }}
      >
        {name}
      </button>
      {open && (
        <div
          role="menu"
          style={{
            position: "absolute",
            left: 0,
            top: "100%",
            width: 236,
            background: "var(--panel)",
            border: "1px solid var(--line-strong)",
            borderRadius: 5,
            boxShadow: "var(--shadow-float)",
            padding: "5px 0",
            zIndex: 60,
          }}
        >
          {items.map((item, index) =>
            item.separator ? (
              <div key={index} style={{ height: 1, background: "var(--line-strong)", margin: "5px 0" }} />
            ) : (
              <div key={index} title={item.hint} style={{ position: "relative" }}
                   onMouseEnter={() => setSubmenu(item.label)}>
                <button
                  type="button"
                  role="menuitem"
                  disabled={item.disabled}
                  onClick={() => {
                    if (item.children) return;
                    item.onClick?.();
                    onClose();
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 10,
                    width: "100%",
                    padding: "6px 12px",
                    border: "none",
                    background: submenu === item.label && !item.disabled ? "var(--hover)" : "transparent",
                    color: item.disabled ? "var(--ink-7)" : "var(--ink)",
                    font: "400 11.5px var(--font-sans)",
                    cursor: item.disabled ? "not-allowed" : "pointer",
                    textAlign: "left",
                  }}
                >
                  <span>{item.label}</span>
                  {item.children ? (
                    <svg width="8" height="8" viewBox="0 0 8 8" aria-hidden>
                      <path d="M2 0 L6 4 L2 8 Z" fill="var(--ink-4)" />
                    </svg>
                  ) : item.shortcut ? (
                    <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-6)" }}>
                      {item.shortcut}
                    </span>
                  ) : null}
                </button>
                {item.children && submenu === item.label && !item.disabled && (
                  <div
                    role="menu"
                    style={{
                      position: "absolute",
                      left: "100%",
                      top: -5,
                      width: 214,
                      background: "var(--panel)",
                      border: "1px solid var(--line-strong)",
                      borderRadius: 5,
                      boxShadow: "var(--shadow-float)",
                      padding: "5px 0",
                    }}
                  >
                    {item.childrenLabel && (
                      <div
                        style={{
                          padding: "4px 12px 6px",
                          font: "600 9.5px var(--font-mono)",
                          color: "var(--ink-6)",
                          letterSpacing: ".08em",
                        }}
                      >
                        {item.childrenLabel}
                      </div>
                    )}
                    {item.children.map((child, childIndex) => (
                      <button
                        key={childIndex}
                        type="button"
                        role="menuitem"
                        disabled={child.disabled}
                        onClick={() => {
                          child.onClick?.();
                          onClose();
                        }}
                        style={{
                          display: "block",
                          width: "100%",
                          padding: "6px 12px",
                          border: "none",
                          background: "transparent",
                          color: child.disabled ? "var(--ink-7)" : "var(--ink)",
                          font: "400 11.5px var(--font-sans)",
                          cursor: child.disabled ? "not-allowed" : "pointer",
                          textAlign: "left",
                        }}
                        onMouseEnter={(event) =>
                          !child.disabled && (event.currentTarget.style.background = "var(--hover)")
                        }
                        onMouseLeave={(event) => (event.currentTarget.style.background = "transparent")}
                      >
                        {child.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ),
          )}
        </div>
      )}
    </div>
  );
}

function WellPicker({
  wells,
  activeWell,
  onChange,
}: {
  wells: Well[];
  activeWell: Well | null;
  onChange: (id: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  return (
    <div ref={ref} style={{ display: "flex", alignItems: "center", gap: 7, position: "relative" }}>
      <span style={{ font: "500 10px var(--font-mono)", color: "var(--ink-6)", letterSpacing: ".06em" }}>
        WELL
      </span>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          height: 26,
          minWidth: 150,
          padding: "0 9px",
          background: "var(--control)",
          border: "1px solid var(--border-control)",
          borderRadius: "var(--radius)",
          boxShadow: "var(--shadow-control)",
          font: "500 11.5px var(--font-sans)",
          color: "var(--ink)",
          cursor: "pointer",
        }}
      >
        <span>{activeWell?.name ?? "— select a well —"}</span>
        <Caret />
      </button>
      {open && (
        <ul
          role="listbox"
          style={{
            position: "absolute",
            top: "calc(100% + 5px)",
            left: 48,
            width: 230,
            listStyle: "none",
            margin: 0,
            padding: 5,
            background: "var(--panel)",
            border: "1px solid var(--border-control)",
            borderRadius: "var(--radius-panel)",
            boxShadow: "var(--shadow-float)",
            zIndex: 60,
            maxHeight: 340,
            overflowY: "auto",
          }}
        >
          {wells.map((well) => (
            <li key={well.id}>
              <button
                type="button"
                role="option"
                aria-selected={well.id === activeWell?.id}
                onClick={() => {
                  onChange(well.id);
                  setOpen(false);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  width: "100%",
                  padding: "6px 9px",
                  border: "none",
                  borderRadius: 5,
                  background: well.id === activeWell?.id ? "var(--selected)" : "transparent",
                  color: "var(--ink-2)",
                  font: "400 11.5px var(--font-sans)",
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    flex: "none",
                    // Panel colour, not status colour: a well given its own
                    // colour on the correlation screen must be recognisable by
                    // that same colour in the project tree, or the colour holds
                    // on one screen only and stops being a marker at all.
                    background:
                      well.panel_color === "none" ? "transparent" : well.panel_color,
                    border: `1.5px solid ${well.panel_color === "none" ? "var(--ink-5)" : "var(--ink-2)"}`,
                  }}
                />
                <span style={{ flex: 1 }}>{well.name}</span>
                <span style={{ font: "400 9.5px var(--font-mono)", color: "var(--ink-6)" }}>
                  {well.curve_sets.reduce((sum, set) => sum + set.curve_count, 0)} curves
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
